import {i18n} from '@online-offer/i18n';
import {Separator} from 'platform/components';
import {Box, Heading, Show, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {css} from 'styled-components';

import {any, isNotNil, values} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {MoneyResponseBody} from '../../api/types/money';
import {CardOo} from '../../components/CardOo/CardOo';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {ReservationRow} from './ReservationRow';

export function ReservationCardPrint() {
  const {
    upsell,
    totalPriceWithVat,
    totalPriceWithoutVat,
    totalDiscountPriceWithVat,
    totalDiscountPriceWithoutVat,
    vehiclePriceWithVat,
    vehiclePriceWithoutVat,
    originalCarPriceWithVat,
    originalCarPriceWithoutVat,
  } = useGetOnlineOfferData();

  const formatCurrency = useFormatCurrency();

  const formatPrice = (price: MoneyResponseBody | Nullish) =>
    price?.amount ? formatCurrency(parseFloat(price.amount), price.currency) : null;

  const isDiscountNonZero = (discount: MoneyResponseBody | Nullish) =>
    Number(discount?.amount ?? 0) > 0;

  if (
    !any(
      isNotNilOrEmpty,
      values([
        upsell,
        totalPriceWithVat,
        totalPriceWithoutVat,
        totalDiscountPriceWithVat,
        totalDiscountPriceWithoutVat,
        vehiclePriceWithVat,
        vehiclePriceWithoutVat,
        originalCarPriceWithVat,
        originalCarPriceWithoutVat,
      ])
    )
  ) {
    return null;
  }

  return (
    <div
      css={css`
        page-break-inside: avoid;
      `}
    >
      <Box width="100%" overflow="hidden" paddingHorizontal={10} paddingVertical={5}>
        <CardOo backgroundColor="semantic.bg.subtle">
          <VStack width="100%" spacing={5}>
            <VStack spacing={2}>
              <Show when={isNotNil(formatPrice(totalPriceWithVat))}>
                <ReservationRow
                  label={i18n.t('reservationCard.totalPrice')}
                  value={<Heading size={3}>{formatPrice(totalPriceWithVat)}</Heading>}
                  labelProps={{alternative: true}}
                />
              </Show>
              <ReservationRow
                label={i18n.t('reservationCard.totalPriceWithoutVat')}
                value={formatPrice(totalPriceWithoutVat)}
              />
            </VStack>
            <Separator spacing={0} />
            <VStack spacing={2}>
              <Show when={isNotNil(formatPrice(vehiclePriceWithVat))}>
                <ReservationRow
                  label={i18n.t('reservationCard.carPrice')}
                  labelProps={{alternative: true}}
                  value={<Heading size={4}>{formatPrice(vehiclePriceWithVat)}</Heading>}
                />
              </Show>
              <ReservationRow
                label={i18n.t('reservationCard.carPriceWithoutVat')}
                value={formatPrice(vehiclePriceWithoutVat)}
              />
              <ReservationRow
                label={i18n.t('reservationCard.originalCarPrice')}
                value={formatPrice(originalCarPriceWithVat ?? originalCarPriceWithoutVat)}
                valueProps={{color: 'danger', strikethrough: true}}
              />
              <Show
                when={
                  isDiscountNonZero(totalDiscountPriceWithVat) ||
                  isDiscountNonZero(totalDiscountPriceWithoutVat)
                }
              >
                <ReservationRow
                  label={i18n.t('reservationCard.customerDiscount')}
                  value={formatPrice(totalDiscountPriceWithVat ?? totalDiscountPriceWithoutVat)}
                  valueProps={{color: 'success'}}
                />
              </Show>
            </VStack>
            <Show when={upsell && upsell.length > 0}>
              <Separator spacing={0} />
              <VStack spacing={2}>
                <ReservationRow
                  label={i18n.t('reservationCard.upsell')}
                  labelProps={{alternative: true}}
                />
                {upsell?.map((singleItem) => {
                  if (
                    isNilOrEmpty(singleItem.name) ||
                    (isNilOrEmpty(singleItem.sellingPrice?.withVat) &&
                      isNilOrEmpty(singleItem.sellingPrice?.withoutVat))
                  ) {
                    return null;
                  }
                  return (
                    <ReservationRow
                      key={singleItem.id}
                      label={singleItem.name!}
                      value={formatPrice(
                        singleItem.sellingPrice?.withVat ?? singleItem.sellingPrice?.withoutVat
                      )}
                    />
                  );
                })}
              </VStack>
            </Show>
          </VStack>
        </CardOo>
      </Box>
    </div>
  );
}
